import { useEffect, useState } from "react";
// Import both the filled and outlined star icons
import { HiOutlineStar, HiStar } from "react-icons/hi";
import { api } from "../services/api";

export default function StarRating({
  value,
  onChange,
  readonly = false,
  size = "md",
  tutorId = null, // New prop to fetch rating from backend
}) {
  const [hoverValue, setHoverValue] = useState(0);
  const [fetchedRating, setFetchedRating] = useState(null);
  const [totalRatings, setTotalRatings] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tutorId) {
      fetchTutorRating();
    }
  }, [tutorId]);

  const fetchTutorRating = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/ratings/tutor/${tutorId}`);
      if (response.success) {
        setFetchedRating(response.averageRating || 0);
        setTotalRatings(response.totalRatings || 0);
      }
    } catch (error) {
      console.error("Error fetching tutor rating:", error);
    } finally {
      setLoading(false);
    }
  };

  // Use fetched rating if available, otherwise use value prop
  const displayRating = fetchedRating !== null ? fetchedRating : value || 0;

  const sizes = {
    sm: "w-5 h-5",
    md: "w-8 h-8",
    lg: "w-12 h-12",
  };

  const handleClick = (rating) => {
    if (!readonly && onChange) {
      onChange(rating);
    }
  };

  const handleMouseEnter = (rating) => {
    if (!readonly) {
      setHoverValue(rating);
    }
  };

  const handleMouseLeave = () => {
    if (!readonly) {
      setHoverValue(0);
    }
  };

  const activeValue = hoverValue || displayRating;

  if (loading) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-sm text-text/60">Loading rating...</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((rating) => (
        <button
          key={rating}
          type="button"
          onClick={() => handleClick(rating)}
          onMouseEnter={() => handleMouseEnter(rating)}
          onMouseLeave={handleMouseLeave}
          disabled={readonly}
          className={`${sizes[size]} transition-all ${
            readonly ? "cursor-default" : "cursor-pointer hover:scale-110"
          }`}
        >
          {/* Conditional rendering of the filled or outlined icon */}
          {rating <= activeValue ? (
            <HiStar className={`w-full h-full text-secondary fill-current`} />
          ) : (
            <HiOutlineStar className={`w-full h-full text-text/30`} />
          )}
        </button>
      ))}
      {displayRating > 0 && (
        <span className="ml-2 text-sm font-medium text-text/70">
          {displayRating.toFixed(1)}
          {totalRatings > 0 && (
            <span className="text-xs text-text/50 ml-1">
              ({totalRatings} {totalRatings === 1 ? "review" : "reviews"})
            </span>
          )}
        </span>
      )}
    </div>
  );
}
